import { NavLink, useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { breakpoints } from '../assets/styles/mediaBreakpoints'
import heroesData from '../assets/data/superheroes.json'
import { useEffect, useState } from 'react'
import { Hero } from '../types/hero'
import { EXTERNAL_LINK } from '../assets/data/external_link'
import { PillButton } from '../components/common/stand_alone/pillButton'

const QuizResultPage = () => {
  const id = useParams().id || ''
  const [heroData, setHeroData] = useState<Hero>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    JSON.parse(JSON.stringify(heroesData)).filter((hero: Hero) => {
      if (hero.id.toString() === id) {
        setHeroData(hero)
      }
    })
  }, [id])

  return (
    <>
      <QuizResultContainer $sdg={id}>
        <HeroImage src={`/images/heroes/SDG_${id}.png`} />
        <HeroPanelContainer>
          <HeroDetailContainer>
            <>
              <HeroTitleContainer>
                <h6>Congratulations. You Are Team {id}!</h6>
                <HeroTitle>{heroData?.name}</HeroTitle>
              </HeroTitleContainer>
              <UNContainer>
                <UNImage src={`/images/un_goals/UN_goals_sdg_${id}.png`} />
                <UNLink to={EXTERNAL_LINK.un_goals + id} target="_blank">
                  SDG{id} - {heroData?.sdg} &gt;
                </UNLink>
              </UNContainer>
              <p>{heroData?.description}</p>
            </>
            <LineBreak />
            <RetakeQuizContainer>
              <h5>You want to explore another superhero?</h5>
              <div style={{ display: 'flex', gap: '16px' }}>
                <PillButton
                  bg="white"
                  textColor="black"
                  title="Retake quiz"
                  onClick={() => setIsModalOpen(true)}
                  additionalCSS={{ padding: '10px 16px' }}
                />
                <SuperheroButton
                  onClick={() => navigate('/quiz-relevant/' + id)}
                >
                  Choose your superhero
                </SuperheroButton>
              </div>
            </RetakeQuizContainer>
          </HeroDetailContainer>
        </HeroPanelContainer>
      </QuizResultContainer>

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setIsModalOpen(false)}>✕</CloseButton>
            <ModalTitle>Do you want to retake the quiz?</ModalTitle>

            <ButtonContainer>
              <YesButton
                onClick={() => {
                  setIsModalOpen(false)
                  navigate('/quiz')
                }}
              >
                Yes
              </YesButton>

              <CancelButton onClick={() => setIsModalOpen(false)}>
                Cancel
              </CancelButton>
            </ButtonContainer>

            <Divider>
              <Line />
              <OrText>or</OrText>
              <Line />
            </Divider>

            <MoreQuizSection>
              <MoreQuizText>
                You can take the quiz more a few question
              </MoreQuizText>
              <TakeMoreQuizButton
                onClick={() => {
                  setIsModalOpen(false)
                  navigate('/quiz-v2')
                }}
              >
                Take more quiz
              </TakeMoreQuizButton>
            </MoreQuizSection>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  )
}

const QuizResultContainer = styled.div.attrs<{ $sdg: string }>((props) => ({
  $sdg: props.$sdg,
}))<{ theme: { colors: { [key: string]: string } } }>`
  width: 100%;
  display: flex;
  gap: 110px;
  padding: 64px;
  background: radial-gradient(
    ellipse at 300px 200px,
    ${(props) => props.theme.colors[`sdg${props.$sdg}-100`]} 5%,
    ${(props) => props.theme.colors[`sdg${props.$sdg}-500`]} 20%,
    ${(props) => props.theme.colors[`sdg${props.$sdg}-700`]} 70%
  );
  @media (max-width: ${breakpoints.desktop}px) {
    flex-direction: column;
    padding: 40px 24px 64px;
    gap: 40px;
    background: radial-gradient(
      circle at 50% 200px,
      ${(props) => props.theme.colors[`sdg${props.$sdg}-100`]} 5%,
      ${(props) => props.theme.colors[`sdg${props.$sdg}-500`]} 15%,
      ${(props) => props.theme.colors[`sdg${props.$sdg}-700`]} 70%
    );
  }
`
const HeroImage = styled.img`
  height: 640px;
  width: 100%;
  object-fit: contain;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 460px;
  }
`
const HeroPanelContainer = styled.div`
  padding-left: 64px;
  padding-right: 64px;
  display: flex;
  flex-direction: column;
  max-width: 592px;
  @media (max-width: ${breakpoints.desktop}px) {
    max-width: auto;
    padding-left: 24px;
    padding-right: 24px;
  }
`
const HeroDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: white;
  gap: 24px;
`
const HeroTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-width: ${breakpoints.desktop}px) {
    gap: 8px;
  }
`
const HeroTitle = styled.h1`
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 40px;
  }
`
const UNContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`
const UNImage = styled.img`
  width: 48px;
  height: 48px;
`
const UNLink = styled(NavLink)`
  color: white;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`
const LineBreak = styled.hr`
  width: 100%;
  color: white;
  font-size: 18px;
`
const RetakeQuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 48px 80px;
  position: relative;
  width: 656px;
  max-width: 90%;
  min-height: 548px;
  background: #dbef9f;
  border-radius: 16px;
  font-family: 'Soleil', sans-serif;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 24px 24px 60px;
    min-height: auto;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 20px 16px 40px;
    width: 95%;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
`

const ModalTitle = styled.h2`
  font-size: 38px;
  text-align: center;
  margin-top: 48px;
  max-width: 100%;
  font-family: 'Soleil', sans-serif;
  font-weight: 600;

  @media (max-width: ${breakpoints.tablet}px) {
    font-size: 32px;
    margin-top: 32px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 24px;
    margin-top: 24px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: ${breakpoints.mobile}px) {
    flex-direction: column;
    gap: 16px;
    width: 100%;
    align-items: center;
  }
`

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  min-width: 150px;
  font-family: 'Soleil', sans-serif;
`

const YesButton = styled(Button)`
  background-color: #333;
  color: white;
  width: 184px;

  &:hover {
    background-color: ${(props) => props.theme.colors['primary-500']};
    color: #333;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    width: 80%;
  }
`

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 2px solid #333;
  color: #333;
  width: 184px;

  &:hover {
    background-color: #333;
    color: white;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    width: 80%;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin: 24px 0;
`

const Line = styled.hr`
  flex: 1;
  border: none;
  height: 1px;
  background-color: #888;
`

const OrText = styled.span`
  font-size: 32px;
  color: #333;
  font-family: 'Soleil';
  font-weight: 600;
`

const MoreQuizSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    gap: 16px;
  }
`

const MoreQuizText = styled.p`
  font-size: 32px;
  color: #333;
  width: 80%;
  font-family: 'Soleil', sans-serif;
  font-weight: 700;

  @media (max-width: ${breakpoints.tablet}px) {
    font-size: 24px;
    width: 100%;
    text-align: center;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 20px;
  }
`

const TakeMoreQuizButton = styled(Button)`
  background-color: transparent;
  border: 2px solid #333;
  padding: 12px 12px;
  max-height: 52px;
  font-size: 12px;
  font-weight: 600;
  color: #333;

  &:hover {
    background-color: #333;
    color: white;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    width: 80%;
  }
`

const SuperheroButton = styled.button`
  background-color: transparent;
  color: white;
  border: 2px solid white;
  border-radius: 999px;
  font-family: 'Soleil', sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 8px 16px;
  &:hover {
    background-color: white;
    color: black;
  }
`

export default QuizResultPage
